import { useNavigation } from '@react-navigation/native'
import { useObserver } from 'mobx-react'
import { fromPromise } from 'mobx-utils'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import '../../ieConfig/MobxConfig'
import { pages } from '../../utils/constants'
import { RootStore, RootType } from './Root'

const StoreContext = createContext<RootType | null>(null)

function useStores<T>(mapStateToProps: (store: RootType) => T): ReturnType<typeof mapStateToProps> {
  const store = useContext(StoreContext)
  if (store === null) {
    throw new Error('Store cannot be null, please add a context provider')
  }
  return useObserver(() => mapStateToProps(store))
}

const useAPIData = (fn: (...args: any) => Promise<unknown>, ...args: any) => {
  const argsRef = useRef<any>(args)
  const callAPI = useMemo(() => fromPromise(fn(...argsRef.current)), [fn])
  const navigation = useNavigation()

  return useObserver(() => {
    const currentState = callAPI?.state
    const data = callAPI?.value
    if (currentState === 'rejected' && data?.isGlobalError) {
      console.log('Error View2')
      navigation.navigate(pages.ErrorView, data)
    }
    return {
      data,
      state: currentState,
    }
  })
}

const useLazyApiData = (
  fn: (...args: any) => Promise<unknown>,
  ...args: Array<any>
): [(...args: Array<any>) => void, { data: any; state: 'pending' | 'fulfilled' | 'rejected' | undefined }] => {
  const [running, setRunning] = useState(0)
  const argsRef = useRef<any>(args)
  const callAPI = useMemo(() => (running > 0 ? fromPromise(fn(...argsRef.current)) : undefined), [fn, running])
  const runFunc = useCallback((...args: any[]) => {
    if (args.length > 0) {
      argsRef.current = [...args]
    }
    setRunning((run) => run + 1)
  }, [])
  const navigation = useNavigation()
  return [
    runFunc,
    useObserver(() => {
      const currentState = callAPI?.state
      const data = callAPI?.value
      if (currentState === 'rejected' && data?.isGlobalError) {
        navigation.navigate(pages.ErrorView, data)
      }
      return {
        data,
        state: currentState,
      }
    }),
  ]
}

const StoreProvider = StoreContext.Provider

export { useStores, StoreProvider, RootStore, useAPIData, useLazyApiData }
